import { resetPassword } from '@aws-amplify/auth';
import { Button, Paper, TextField, Typography, useMediaQuery } from '@mui/material';
import { emailRegex } from 'components/Drawer/variants/externalMembers/shared';
import useEnqueueSnackbar from 'hooks/useEnqueueSnackbar';
import useTranslation from 'hooks/useTranslation/useTranslation';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import Background from 'ui/atoms/Background/Background';
import Icon from 'ui/atoms/Icon/Icon';
import ComponentWrapper from 'ui/templates/ComponentWrapper/ComponentWrapper';

const ForgotPassword: React.FC<React.PropsWithChildren> = () => {
  const { translation } = useTranslation();
  const navigate = useNavigate();

  const enqueueSnackbar = useEnqueueSnackbar();
  const handleFetchError = (errorMessage: string) => enqueueSnackbar(errorMessage, { snackbartype: 'error' });

  const [email, setEmail] = useState<string>('');
  const [isEmailSend, setIsEmailSend] = useState(false);
  const [showValidation, setShowValidation] = useState(false);
  const [emailInputValidation, setEmailInputValidation] = useState<string | undefined>(undefined);

  const handleForgotPassword = async () => {
    setShowValidation(true);
    if (email && emailInputValidation === undefined) {
      try {
        await resetPassword({ username: email });
        setIsEmailSend(true);
      } catch (err) {
        handleFetchError('' + err);
      }
    }
  };

  const handleGoBackToLoginPage = () => {
    navigate('/');
  };

  const isExtraSmallScreen = useMediaQuery('(min-width: 0px) and (max-width:627px)');
  const isSmallScreen = useMediaQuery('(min-width: 628px) and (max-width:643px)');
  const isMediumScreen = useMediaQuery('(min-width: 644px) and (max-width:663px)');

  const getPaperPadding = () => {
    if (isExtraSmallScreen || isSmallScreen) return '48px 24px';
    if (isMediumScreen) return '48px 40px';
    return '48px 80px';
  };

  const getPaperWidth = () => {
    if (isExtraSmallScreen) {
      return 'calc(100vw - 80px)';
    }
    if (isSmallScreen) {
      return '520px';
    }
    if (isMediumScreen) {
      return '552px';
    }
    return '632px';
  };

  useEffect(() => {
    if (email === '') setEmailInputValidation(translation.email_required_error);
    else if (!email?.toLowerCase().match(emailRegex)) setEmailInputValidation(translation.email_invalid_error);
    else setEmailInputValidation(undefined);
  }, [email, translation.email_required_error, translation.email_invalid_error]);

  return (
    <ComponentWrapper width="100vw" height="100vh" alignItems="center" justifyContent="center">
      <Background id="background" />
      <Paper
        elevation={3}
        data-testid="login-window"
        id="login-window"
        style={{ padding: getPaperPadding(), width: getPaperWidth(), borderRadius: '10px' }}
      >
        <ComponentWrapper flexDirection="column" gap="40px">
          <ComponentWrapper flexDirection="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h4">ATLAS</Typography>
            <Icon name="TKHSecurityLogo" width={106} height={40} viewBox="0 0 170 64" />
          </ComponentWrapper>
          {isEmailSend ? (
            <ComponentWrapper flexDirection="column">
              <Typography variant="body2">{translation.check_your_email}</Typography>
              <Typography variant="body2">
                {translation.email_sent_to} {email} {translation.reset_email_link_info}.
                <br />
                <br />
                {translation.no_email_received}
              </Typography>
              <Button onClick={handleForgotPassword} size="large" color="primary" style={{ marginTop: '40px' }}>
                {translation.resend_email}
              </Button>
            </ComponentWrapper>
          ) : (
            <ComponentWrapper flexDirection="column" gap="40px">
              <Typography variant="body2">{translation.reset_password_instructions}</Typography>
              <ComponentWrapper flexDirection="column" gap="24px">
                <TextField
                  type="email"
                  label={translation.email_address}
                  size="medium"
                  variant="outlined"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  error={showValidation && emailInputValidation !== undefined}
                  helperText={showValidation ? emailInputValidation : undefined}
                />
                <Button
                  onClick={handleForgotPassword}
                  variant="contained"
                  size="large"
                  color="primary"
                  style={{ color: '#373737' }}
                  id="sign-in-button"
                  disabled={emailInputValidation !== undefined && showValidation}
                >
                  {translation.reset_password}
                </Button>
                <Button onClick={handleGoBackToLoginPage} size="large" id="sign-in-button">
                  <Typography variant="button" color="textPrimary">
                    {translation.back_to_sign_in}
                  </Typography>
                </Button>
              </ComponentWrapper>
            </ComponentWrapper>
          )}
        </ComponentWrapper>
      </Paper>
    </ComponentWrapper>
  );
};

export default ForgotPassword;
